import React, { useState } from 'react';
import { Button } from './Button';
import { ArrowRightIcon, ArrowLeftIcon, BrainIcon, HeartIcon, BookOpenIcon, HeadphonesIcon, MessageSquareIcon, CheckIcon, SettingsIcon, UserIcon } from 'lucide-react';
interface OnboardingProps {
  onComplete: (userData: any) => void;
  onSkip: () => void;
}
const Onboarding: React.FC<OnboardingProps> = ({
  onComplete,
  onSkip
}) => {
  const [step, setStep] = useState(1);
  const [userData, setUserData] = useState({
    name: '',
    goals: [],
    preferredThemes: [],
    notificationPreference: 'daily',
    primaryGoal: ''
  });
  const totalSteps = 5;
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onComplete(userData);
    }
  };
  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserData({
      ...userData,
      [e.target.name]: e.target.value
    });
  };
  const toggleGoal = (goal: string) => {
    const newGoals = userData.goals.includes(goal) ? userData.goals.filter(g => g !== goal) : [...userData.goals, goal];
    setUserData({
      ...userData,
      goals: newGoals
    });
  };
  const toggleTheme = (theme: string) => {
    const newThemes = userData.preferredThemes.includes(theme) ? userData.preferredThemes.filter(t => t !== theme) : [...userData.preferredThemes, theme];
    setUserData({
      ...userData,
      preferredThemes: newThemes
    });
  };
  const setNotificationPreference = (pref: string) => {
    setUserData({
      ...userData,
      notificationPreference: pref
    });
  };
  const setPrimaryGoal = (goal: string) => {
    setUserData({
      ...userData,
      primaryGoal: goal
    });
  };
  // Calculate progress percentage
  const progressPercentage = step / totalSteps * 100;
  const renderStep = () => {
    switch (step) {
      case 1:
        return <div className="space-y-8" data-id="element-635">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-636">
              Welkom bij Mosy
            </h2>
            <div className="flex justify-center" data-id="element-637">
              
            </div>
            <p className="text-center text-gray-600" data-id="element-638">
              Mosy is je persoonlijke mentale gezondheidscoach die je helpt om
              inzicht te krijgen in je gedachten, gevoelens en patronen.
            </p>
            <div className="space-y-4" data-id="element-639">
              <div className="flex items-center p-4 border border-gray-200 rounded-lg" data-id="element-640">
                <div className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center mr-4" data-id="element-641">
                  <HeartIcon className="w-5 h-5 text-gray-600" data-id="element-642" />
                </div>
                <div data-id="element-643">
                  <p className="text-sm font-medium text-gray-800" data-id="element-644">
                    Dagelijkse check-ins
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-645">
                    Volg je mentale welzijn met eenvoudige check-ins
                  </p>
                </div>
              </div>
              <div className="flex items-center p-4 border border-gray-200 rounded-lg" data-id="element-646">
                <div className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center mr-4" data-id="element-647">
                  <BookOpenIcon className="w-5 h-5 text-gray-600" data-id="element-648" />
                </div>
                <div data-id="element-649">
                  <p className="text-sm font-medium text-gray-800" data-id="element-650">
                    Oefeningen & thema's
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-651">
                    Ontdek oefeningen voor verschillende mentale uitdagingen
                  </p>
                </div>
              </div>
              <div className="flex items-center p-4 border border-gray-200 rounded-lg" data-id="element-652">
                <div className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center mr-4" data-id="element-653">
                  <HeadphonesIcon className="w-5 h-5 text-gray-600" data-id="element-654" />
                </div>
                <div data-id="element-655">
                  <p className="text-sm font-medium text-gray-800" data-id="element-656">
                    Soundscapes & meditaties
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-657">
                    Ontspan met rustgevende geluiden en geleide meditaties
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-658">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-659">
              Personaliseer je ervaring
            </h2>
            <div className="space-y-4" data-id="element-660">
              <label className="block text-sm font-medium text-gray-700" data-id="element-661">
                Hoe wil je dat we je noemen?
              </label>
              <input type="text" name="name" value={userData.name} onChange={handleInputChange} placeholder="Je naam" className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400" data-id="element-662" />
            </div>
            <div className="space-y-4" data-id="element-663">
              <p className="text-sm font-medium text-gray-700" data-id="element-664">
                Wat is je primaire doel met Mosy?
              </p>
              <div className="grid grid-cols-1 gap-3" data-id="element-665">
                {['Stress verminderen', 'Beter slapen', 'Meer focus', 'Angst beheersen', 'Emoties reguleren', 'Algemeen welzijn verbeteren'].map(goal => <button key={goal} className={`p-3 text-left border-2 rounded-lg transition-colors ${userData.primaryGoal === goal ? 'border-gray-500 bg-gray-100' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => setPrimaryGoal(goal)} data-id="element-666">
                    {goal}
                  </button>)}
              </div>
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-667">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-668">
              Jouw interesses
            </h2>
            <p className="text-center text-gray-600 mb-4" data-id="element-669">
              Selecteer thema's die je interesseren zodat we je ervaring kunnen
              personaliseren.
            </p>
            <div className="grid grid-cols-2 gap-3" data-id="element-670">
              {['Mindfulness', 'Slaap', 'Stress', 'Focus', 'Relaties', 'Angst', 'Depressie', 'Zelfzorg', 'Werk-privé balans', 'Emotieregulatie'].map(theme => <button key={theme} className={`p-3 border-2 rounded-lg transition-colors flex items-center justify-between ${userData.preferredThemes.includes(theme) ? 'border-gray-500 bg-gray-100' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => toggleTheme(theme)} data-id="element-671">
                  <span className="text-sm" data-id="element-672">{theme}</span>
                  {userData.preferredThemes.includes(theme) && <CheckIcon className="w-4 h-4 text-gray-700" data-id="element-673" />}
                </button>)}
            </div>
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-674">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-675">
              Check-in voorkeuren
            </h2>
            <p className="text-center text-gray-600 mb-4" data-id="element-676">
              Hoe vaak wil je herinnerd worden om in te checken?
            </p>
            <div className="space-y-3" data-id="element-677">
              {[{
              id: 'daily',
              label: 'Dagelijks'
            }, {
              id: 'weekdays',
              label: 'Alleen werkdagen'
            }, {
              id: 'weekly',
              label: 'Wekelijks'
            }, {
              id: 'none',
              label: 'Geen herinneringen'
            }].map(option => <button key={option.id} className={`w-full p-4 text-left border-2 rounded-lg transition-colors flex items-center justify-between ${userData.notificationPreference === option.id ? 'border-gray-500 bg-gray-100' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => setNotificationPreference(option.id)} data-id="element-678">
                  <span data-id="element-679">{option.label}</span>
                  {userData.notificationPreference === option.id && <CheckIcon className="w-5 h-5 text-gray-700" data-id="element-680" />}
                </button>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-8" data-id="element-681">
            <h2 className="text-2xl font-light text-gray-800 text-center" data-id="element-682">
              Je bent klaar om te beginnen!
            </h2>
            <div className="flex justify-center" data-id="element-683">
              <div className="w-20 h-20 rounded-full border-2 border-gray-300 bg-gray-100 flex items-center justify-center" data-id="element-684">
                <CheckIcon className="w-10 h-10 text-gray-700" data-id="element-685" />
              </div>
            </div>
            <p className="text-center text-gray-600" data-id="element-686">
              {userData.name ? `Hallo ${userData.name}! ` : ''}We hebben je
              ervaring gepersonaliseerd op basis van je voorkeuren. Je kunt deze
              instellingen altijd aanpassen in je profiel.
            </p>
            <div className="space-y-4" data-id="element-687">
              <div className="p-4 border border-gray-200 rounded-lg" data-id="element-688">
                <p className="text-sm font-medium text-gray-800 mb-2" data-id="element-689">
                  Je primaire doel
                </p>
                <p className="text-sm text-gray-600" data-id="element-690">
                  {userData.primaryGoal || 'Algemeen welzijn verbeteren'}
                </p>
              </div>
              {userData.preferredThemes.length > 0 && <div className="p-4 border border-gray-200 rounded-lg" data-id="element-691">
                  <p className="text-sm font-medium text-gray-800 mb-2" data-id="element-692">
                    Je interesses
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-693">
                    {userData.preferredThemes.map(theme => <span key={theme} className="text-xs px-2 py-1 bg-gray-100 border border-gray-200 rounded-md" data-id="element-694">
                        {theme}
                      </span>)}
                  </div>
                </div>}
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full max-w-xl mx-auto py-8" data-id="element-695">
      <div className="mb-8" data-id="element-696">
        <div className="flex justify-between items-center mb-2" data-id="element-697">
          <span className="text-sm text-gray-500" data-id="element-698">
            Stap {step} van {totalSteps}
          </span>
          <span className="text-sm text-gray-500" data-id="element-699">
            {Math.round(progressPercentage)}%
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-700">
          <div className="h-full bg-gray-700 transition-all duration-300 ease-out" style={{
          width: `${progressPercentage}%`
        }} data-id="element-701"></div>
        </div>
      </div>
      <div className="min-h-[400px] flex flex-col" data-id="element-702">{renderStep()}</div>
      <div className="mt-12 flex justify-between" data-id="element-703">
        {step > 1 ? <Button variant="secondary" leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-705" />} onClick={handlePrevious} data-id="element-704">
              Vorige
            </Button> : <div data-id="element-706"></div> // Empty div to maintain flex spacing
      }
        <div className="flex space-x-3" data-id="element-707">
          {step === 1 && <Button variant="tertiary" onClick={onSkip} data-id="element-708">
              Overslaan
            </Button>}
          <Button rightIcon={<ArrowRightIcon className="w-4 h-4" data-id="element-710" />} onClick={handleNext} data-id="element-709">
            {step < totalSteps ? 'Volgende' : 'Aan de slag'}
          </Button>
        </div>
      </div>
    </div>;
};
export default Onboarding;